import React, { useEffect, useState } from "react";
import InputField from "../shared-components/InputField";

import { classNames, uniqueUUID } from "../utils";
import AddButton from "../shared-components/AddButton";
import { XIcon } from "../shared-components/icons";
import { ButtonColorTypes, ButtonTypes } from "../shared-components/Button";
import Button from "../shared-components/Button";
import { AdditionalInfoData, useReportsWizard } from "./ReportsWizardProvider";
import { textWithMaxLength } from "./ReportsWizardUtil";
import ReportsWizardAddressFields from "./ReportsWizardAddressFields";

type Props = {};

interface AddressModel {
  key?: string;
  street: string;
  city: string;
  state: string;
  zip: string;
  country: string;
}
interface JobModel {
  key?: string;
  company: string;
  title: string;
  startDate?: string;
  endDate?: string;
  country: string;
}

interface DegreeModel {
  key?: string;
  institution: string;
  degree: string;
  startDate?: string;
  endDate?: string;
  country: string;
}

const addressFieldKeys: {
  key: "street" | "city" | "state" | "zip" | "country" | "key";
  label: string;
  type?: string;
}[] = [
  { key: "street", label: "Street Address" },
  { key: "city", label: "City/Village" },
  { key: "state", label: "State/Province" },
  { key: "zip", label: "Postal Code" },
  { key: "country", label: "Country" },
];

const jobFieldKeys: {
  key: "company" | "title" | "startDate" | "endDate" | "country" | "key";
  label: string;
  type?: string;
}[] = [
  { key: "company", label: "Company" },
  { key: "title", label: "Title" },
  { key: "startDate", label: "Start Date", type: "date" },
  { key: "endDate", label: "End Date", type: "date" },
  { key: "country", label: "Country" },
];

const degreeFieldKeys: {
  key: "institution" | "degree" | "startDate" | "endDate" | "country" | "key";
  label: string;
  type?: string;
}[] = [
  { key: "institution", label: "Institution" },
  { key: "degree", label: "Degree" },
  { key: "startDate", label: "Start Date", type: "date" },
  { key: "endDate", label: "End Date", type: "date" },
  { key: "country", label: "Country" },
];

function isValidField<T>(
  field: T,
  fieldKeys: { key: string & keyof T; label: string; type?: string }[]
): boolean {
  // at least one field is required
  return fieldKeys.some((fieldKey) => {
    const fieldValue = field[fieldKey.key as keyof T];
    return fieldValue !== "";
  });
}

export const ReportsWizardAdditionalInfo = (props: Props) => {
  const { nextStep, previousStep, setAdditionalInfo, getAdditionalInfo } =
    useReportsWizard();
  const [state, setState] = useState<AdditionalInfoData>(() => {
    const additionalInfo = getAdditionalInfo();
    if (additionalInfo) {
      return {
        addresses: additionalInfo.addresses.map((address) => ({
          ...address,
        })),
        jobs: additionalInfo.jobs.map((job) => ({
          ...job,

          startDate: job.startDate ? job.startDate : undefined,
          endDate: job.endDate ? job.endDate : undefined,
        })),
        degrees: additionalInfo.degrees.map((degree) => ({
          ...degree,

          startDate: degree.startDate ? degree.startDate : undefined,
          endDate: degree.endDate ? degree.endDate : undefined,
        })),
      };
    }
    return {
      addresses: [],
      jobs: [],
      degrees: [],
    };
  });

  const [isDirty, setIsDirty] = useState(false);
  const [isValid, setIsValid] = useState(false);

  const handleNextStep = () => {
    setIsDirty(true);
    if (isValid) {
      nextStep(isValid, true);
    }
  };

  const handleFieldsChange = (
    key: keyof AdditionalInfoData,
    fields: AdditionalInfoData[keyof AdditionalInfoData]
  ) => {
    const newState = { ...state };
    if (key === "addresses") {
      newState.addresses = fields as AdditionalInfoData["addresses"];
    } else if (key === "jobs") {
      newState.jobs = fields as AdditionalInfoData["jobs"];
    } else if (key === "degrees") {
      newState.degrees = fields as AdditionalInfoData["degrees"];
    }
    setState(newState);
    setAdditionalInfo(newState);
  };

  useEffect(() => {
    const jobsError = state.jobs.some(
      (job) => !isValidField<JobModel>(job, jobFieldKeys)
    );
    const degreesError = state.degrees.some(
      (degree) => !isValidField<DegreeModel>(degree, degreeFieldKeys)
    );
    const addressesError = state.addresses.some(
      (address) => !isValidField<AddressModel>(address, addressFieldKeys)
    );
    const hasError = jobsError || degreesError || addressesError;
    setIsValid(!hasError);
  }, [state.addresses, state.jobs, state.degrees]);

  return (
    <div>
      <span className="text-lg font-bold text-darkBlue">Address</span>
      <div className="gap-2 p-4 border border-gray-200 rounded-lg mt-4 bg-gray-100">
        <ReportsWizardAddressFields
          fieldKeys={addressFieldKeys}
          buttonText="Add Address"
          label="Address"
          buildEmptyDefinition={() => ({
            key: uniqueUUID(),
            street: "",
            city: "",
            state: "",
            zip: "",
            country: "",
          })}
          skipInitialFill={true}
          onChange={(fields) => {
            handleFieldsChange("addresses", fields);
          }}
          isDirty={isDirty}
          prefill={state.addresses.map((address) => ({
            ...address,
            key: uniqueUUID(),
          }))}
          validateFieldsRow={(fields) => isValidField(fields, addressFieldKeys)}
        />
      </div>
      <div className="text-lg font-bold text-darkBlue pt-8">Employment</div>

      <div className="gap-2 p-4 border border-gray-200 rounded-lg mt-4 bg-gray-100">
        <MultiAdd<JobModel>
          fieldKeys={jobFieldKeys}
          buttonText="Add Job"
          label="Job"
          buildEmptyDefinition={() => ({
            key: uniqueUUID(),
            company: "",
            title: "",
            startDate: "",
            endDate: "",
            country: "",
          })}
          skipInitialFill={true}
          onChange={(fields) => {
            handleFieldsChange("jobs", fields);
          }}
          prefill={state.jobs.map((job) => ({
            ...job,
            key: uniqueUUID(),
          }))}
          isDirty={isDirty}
          validateFieldsRow={(fields) => isValidField(fields, jobFieldKeys)}
        />
      </div>
      <div className="text-lg font-bold text-darkBlue pt-8">Education</div>
      <div className="gap-2 p-4 border border-gray-200 rounded-lg mt-4 bg-gray-100">
        <MultiAdd<DegreeModel>
          fieldKeys={degreeFieldKeys}
          buttonText="Add Education"
          label="Education"
          skipInitialFill={true}
          buildEmptyDefinition={() => ({
            key: uniqueUUID(),
            institution: "",
            degree: "",
            startDate: "",
            endDate: "",
            country: "",
          })}
          onChange={(fields) => {
            handleFieldsChange("degrees", fields);
          }}
          isDirty={isDirty}
          prefill={state.degrees.map((degree) => ({
            ...degree,
            key: uniqueUUID(),
          }))}
          validateFieldsRow={(fields) => isValidField(fields, degreeFieldKeys)}
        />
      </div>
      <div className="flex justify-between pt-8">
        <Button
          type={ButtonTypes.default}
          text={"Previous Step"}
          colorType={ButtonColorTypes.alternate}
          onClick={() => previousStep()}
        />
        <Button
          text={"Next Step"}
          colorType={ButtonColorTypes.custom}
          isRoundedFull={true}
          className="bg-orange-500 text-white rounded-full ml-auto w-56"
          onClick={handleNextStep}
          disabled={isDirty && !isValid}
        />
      </div>
    </div>
  );
};

function MultiAdd<T extends { key?: string }>({
  onChange,
  prefill,
  buildEmptyDefinition,
  fieldKeys,
  buttonText,
  skipInitialFill = false,
  isDirty,
  isValid,
  validateFieldsRow,
}: {
  onChange?: (fields: T[]) => void;
  prefill?: T[];
  alternateColor?: boolean;
  buildEmptyDefinition: () => T;
  fieldKeys: { key: string & keyof T; label: string; type?: string }[];
  buttonText: string;
  label: string;
  skipInitialFill?: boolean;
  isDirty?: boolean;
  isValid?: boolean;
  validateFieldsRow?: (fields: T) => boolean;
}) {
  const [fieldsList, setFields] = useState<T[]>(
    skipInitialFill
      ? prefill || []
      : prefill
      ? [...prefill]
      : [buildEmptyDefinition()]
  );

  const appendFields = () => {
    handleChange([...fieldsList, buildEmptyDefinition()]);
  };

  const updateField = (idx: number, field: string, value: string) => {
    fieldsList[idx][field as keyof T] = value as T[keyof T];
    handleChange([...fieldsList]);
  };

  const removeField = (idx: number) => {
    fieldsList.splice(idx, 1);
    handleChange([...fieldsList]);
  };

  const handleChange = (list: T[]) => {
    onChange?.(list);
    setFields(list);
  };

  const handleAdd = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.preventDefault();
    appendFields();
  };

  const handleRemove = (idx: number) => {
    removeField(idx);
  };

  return (
    <div className="flex flex-col">
      <div className="flex-1">
        {fieldsList.map((fields, idx) => (
          <>
            <div
              key={idx}
              className={classNames(
                "flex flex-wrap items-center",
                idx > 0 ? "mt-4" : ""
              )}
            >
              <div className="flex-1 flex gap-4 flex-wrap  items-center mr-4">
                {fieldKeys.map((fieldKey) => (
                  <InputField
                    handleChange={(e) => {
                      updateField(
                        idx,
                        fieldKey.key,
                        textWithMaxLength(e.target.value)
                      );
                    }}
                    name={`${fieldKey.key}-${idx}`}
                    placeholder=""
                    value={fieldsList[idx][fieldKey.key] as string}
                    label={fieldKey.label}
                    type={fieldKey.type || "text"}
                    classes="w-72 h-[70px]"
                    className={classNames(
                      isDirty && !validateFieldsRow?.(fields)
                        ? "border-red-500"
                        : "",
                      "w-full"
                    )}
                  />
                ))}
                <span className="pl-2 h-0 w-10">
                  {(skipInitialFill || (!skipInitialFill && idx > 0)) && (
                    <div
                      className="rounded-full bg-gray-50 border p-1 text-gray-500 cursor-pointer"
                      onClick={(e) => handleRemove(idx)}
                    >
                      <XIcon />
                    </div>
                  )}
                </span>
              </div>
            </div>
            {isDirty && !validateFieldsRow?.(fields) && (
              <div className="text-red-500 text-sm mt-2">
                At least one of the fields shouldn't be blank. Use N/A if not
                applicable
              </div>
            )}
          </>
        ))}
      </div>
      <div className="mt-4">
        <AddButton text={buttonText} onClick={handleAdd} />
      </div>
    </div>
  );
}
